import { useEffect, useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { AlertCircle, Download, Loader2, RefreshCw, Search } from 'lucide-react'
import { getLogList, getLogText, logDownloadUrl } from '../../lib/api/client'
import { PageHead, Switch } from './ui'
import { cn } from '../../lib/cn'

const LINE_OPTS = [50, 100, 200, 1000]

const LEVEL_RE = /\b(EMERGENCY|CRITICAL|CRIT|ALERT|ERROR|ERR|WARNING|WARN|NOTICE|INFO|DEBUG|LOGIN|LOGOUT|INSERT|UPDATE|DELETE)\b/

function levelColor(line: string): string {
  const lv = (line.match(LEVEL_RE)?.[1] ?? '').toUpperCase()
  if (/EMERGENCY|CRITICAL|CRIT|ALERT|ERROR|ERR|DELETE/.test(lv)) return 'text-[#ff7a7a]'
  if (/WARNING|WARN/.test(lv)) return 'text-[#f5bd5c]'
  if (/NOTICE|INSERT|UPDATE/.test(lv)) return 'text-[#74d99f]'
  if (/INFO|LOGIN|LOGOUT/.test(lv)) return 'text-[#67ccdd]'
  if (/DEBUG/.test(lv)) return 'text-[#8b8b94]'
  return 'text-[#d4d4d8]'
}

function splitTimestamp(line: string): { ts: string; rest: string } {
  let m = line.match(/^\[[^\]]+\]/)
  if (m) return { ts: m[0], rest: line.slice(m[0].length) }
  m = line.match(/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/)
  if (m) return { ts: m[0], rest: line.slice(m[0].length) }
  return { ts: '', rest: line }
}

function prettyName(id: string): string {
  return id.replace(/^garagesanmarco-/, '').replace(/\.log$/i, '')
}

export function LogPage() {
  const [file, setFile] = useState<string | null>(null)
  const [lines, setLines] = useState(200)
  const [filter, setFilter] = useState('')
  const [auto, setAuto] = useState(false)

  const files = useQuery({ queryKey: ['logList'], queryFn: getLogList, staleTime: 60_000 })

  const fileOptions = useMemo(() => {
    return [...(files.data ?? [])].map((f) => f.id).filter(Boolean).sort((a, b) => b.localeCompare(a))
  }, [files.data])

  useEffect(() => {
    if (!file && fileOptions.length > 0) setFile(fileOptions[0])
  }, [fileOptions, file])

  const log = useQuery({
    queryKey: ['logText', file, lines],
    queryFn: () => getLogText(file!, lines),
    enabled: Boolean(file),
    placeholderData: keepPreviousData,
    refetchInterval: auto ? 4000 : false,
  })

  const allLines = useMemo(() => (log.data ?? '').split('\n'), [log.data])
  const shown = useMemo(() => {
    const n = filter.trim().toLowerCase()
    const ls = n ? allLines.filter((l) => l.toLowerCase().includes(n)) : allLines
    // Trim a trailing empty line for a tidy count.
    return ls.length > 1 && ls[ls.length - 1] === '' ? ls.slice(0, -1) : ls
  }, [allLines, filter])

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Configurazione · Log"
        description="Log applicativi e di audit del gestionale. Seleziona il file, scegli quante righe finali leggere e filtra il contenuto."
      />

      {/* toolbar */}
      <div className="mb-[14px] flex flex-wrap items-center gap-2.5">
        <select
          value={file ?? ''}
          onChange={(e) => setFile(e.target.value)}
          disabled={files.isLoading || fileOptions.length === 0}
          className="h-10 max-w-[280px] rounded-lg border border-line bg-card px-3 text-[13px] font-medium text-coal outline-none focus:border-verdigris disabled:opacity-50"
        >
          {files.isLoading ? (
            <option>Caricamento…</option>
          ) : fileOptions.length === 0 ? (
            <option>Nessun log</option>
          ) : (
            fileOptions.map((id) => (
              <option key={id} value={id}>
                {prettyName(id)}
              </option>
            ))
          )}
        </select>

        <div className="inline-flex overflow-hidden rounded-lg border border-line bg-card">
          {LINE_OPTS.map((n) => (
            <button
              key={n}
              onClick={() => setLines(n)}
              className={cn(
                'px-3 py-2 text-[12.5px] font-semibold transition-colors',
                lines === n ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
              )}
            >
              {n}
            </button>
          ))}
        </div>

        <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtra righe… (es. ERROR, UPDATE, IP)"
            className="h-10 w-full rounded-lg border border-line bg-card pl-9 pr-3 text-[13px] text-coal outline-none placeholder:text-faint focus:border-verdigris"
          />
        </div>

        <label className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-slate">
          <Switch on={auto} onClick={() => setAuto((a) => !a)} label="Auto-aggiornamento" />
          Auto
        </label>

        <button
          onClick={() => log.refetch()}
          disabled={!file || log.isFetching}
          className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-card px-3.5 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', log.isFetching && 'animate-spin')} /> Aggiorna
        </button>

        {file && (
          <a
            href={logDownloadUrl(file, Math.max(lines, 1000))}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-card px-3.5 text-[13px] font-semibold text-slate hover:border-faint"
          >
            <Download className="h-4 w-4" /> Grezzo
          </a>
        )}
      </div>

      {/* console */}
      <div className="overflow-hidden rounded-card border border-[#26262b] bg-[#151517] shadow-soft">
        <div className="flex items-center gap-2 border-b border-[#26262b] px-4 py-2 text-[11.5px] font-semibold text-[#8b8b94]">
          <span className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          </span>
          <span className="ml-1 font-mono">{file ? prettyName(file) : '—'}</span>
          <span className="ml-auto tnum">
            {shown.length.toLocaleString('it-IT')} {filter ? 'righe filtrate' : 'righe'}
          </span>
        </div>
        <div className="max-h-[60vh] min-h-[320px] overflow-auto p-4 font-mono text-[12px] leading-[1.55]">
          {log.isError ? (
            <div className="flex items-center gap-2 text-[#ff7a7a]">
              <AlertCircle className="h-4 w-4" /> Impossibile caricare il log.
            </div>
          ) : !file ? (
            <div className="text-[#8b8b94]">Nessun file di log disponibile.</div>
          ) : log.isLoading ? (
            <div className="flex items-center gap-2 text-[#8b8b94]">
              <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
            </div>
          ) : shown.length === 0 || (shown.length === 1 && shown[0] === '') ? (
            <div className="text-[#8b8b94]">{filter ? 'Nessuna riga corrisponde al filtro.' : 'Log vuoto.'}</div>
          ) : (
            shown.map((line, i) => {
              const { ts, rest } = splitTimestamp(line)
              return (
                <div key={i} className="whitespace-pre-wrap break-words">
                  {ts && <span className="text-[#6a6a73]">{ts}</span>}
                  <span className={levelColor(line)}>{rest || (ts ? '' : ' ')}</span>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
